/**
 * Modal Handler - Event Handler Implementation
 * Handles user interactions and events for element modal
 * Public modal behavior implementation
 */

import { BoardId } from "../constants/board-id";
import { ModalApiService } from "./modal-api";
import type { ModalPostData, ModalReadPayload, ModalRender } from "./modal-render";
import { notifier } from "../core/notifier/notifier";
import { translator } from "../translation/translator";
import { getPostShowSetup } from "../post/show/post-show-setup";
import { PostShowModalSetup } from "../post/show/post-show-modal-setup";
import { phpAuthUser } from "../core/php-data-access";
import { disposeEventScope } from "../core/event-scope/event-scope";
import { perfLogger } from "../core/perf-logger";
import { busyCursor } from "../util/busy-cursor/busy-cursor";
import { spinner } from "../util/spinner/spinner";
import { ModalStateHandler } from "./modal-state-handler";
import { ModalForm } from "./modal-form";
import { CommonUtils } from "../util/common-utils";
import { PostEditSetup } from "../post/edit/post-edit-setup";

class ModalHandler {
    private static readonly READ_QUALIFICATION_MS = 10000;

    private render: ModalRender;
    public readonly state: ModalStateHandler;
    private currentlyOpeningPostId: number | null;
    private pendingReadRequests: Set<number>;
    private readTimers: Map<number, ReturnType<typeof setTimeout>>;
    private _modalOpenBusyToken: string | null;
    private readonly modalForm: ModalForm;

    public constructor(render: ModalRender, state: ModalStateHandler) {
        this.render = render;
        this.state = state;
        this.currentlyOpeningPostId = null;
        this.pendingReadRequests = new Set();
        this.readTimers = new Map(); // Store timer IDs for cancellation

        this._modalOpenBusyToken = null;
        this.modalForm = new ModalForm();
    }

    private toPostId(rawPostId: string | undefined): number | null {
        const normalizedPostId = Number(rawPostId);
        return Number.isInteger(normalizedPostId) && normalizedPostId > 0 ? normalizedPostId : null;
    }

    // Create forms are addressed by a UI key, separate from persisted numeric post_id.
    // Example: modalContentId = "review-create-5" while post_id remains 5.
    private toModalContentId(rawModalContentId: string | undefined): string | null {
        const value = String(rawModalContentId || "").trim();
        return value !== "" ? value : null;
    }

    /**
     * @param {HTMLFormElement|null|undefined} formEl
     * @returns {boolean}
     */
    private isReviewCreateForm(formEl: HTMLFormElement | null | undefined): boolean {
        if (!(formEl instanceof HTMLFormElement)) return false;
        const boardInput = this.modalForm.getBoardIdInput(formEl);
        const boardId = String(
            (boardInput instanceof HTMLInputElement ? boardInput.value : "") ||
                formEl.getAttribute("data-board-id") ||
                "",
        )
            .trim()
            .toLowerCase();
        return boardId === BoardId.REVIEW;
    }

    /**
     * Reset contextual classes on shared element modal shell.
     */
    private resetModalContextClasses(): void {
        const overlay = this.render.getOverlay?.();
        const modal = this.render.getModal?.();
        overlay?.classList.remove("element-modal-overlay--post-create");
        modal?.classList.remove("element-modal--post-create");
        overlay?.classList.remove("element-modal-overlay--review-create");
        modal?.classList.remove("element-modal--review-create");
        overlay?.classList.remove("element-modal-overlay--review");
        modal?.classList.remove("element-modal--review");
    }

    /**
     * Apply contextual classes for create-review modal and review-board modals.
     * @param {HTMLElement} link
     */
    private applyModalContextClasses(link: HTMLElement): void {
        this.resetModalContextClasses();

        const modalKind = link?.dataset?.modalKind;
        const boardId = link?.dataset?.boardId;
        const overlay = this.render.getOverlay?.();
        const modal = this.render.getModal?.();

        if (modalKind === "post-create") {
            overlay?.classList.add("element-modal-overlay--post-create");
            modal?.classList.add("element-modal--post-create");
        }

        if (modalKind === "post-create" && boardId === BoardId.REVIEW) {
            overlay?.classList.add("element-modal-overlay--review-create");
            modal?.classList.add("element-modal--review-create");
        }

        // Review posts always render at extra-large size; keep them top-left anchored
        // (rather than the default top-center) both when creating and when viewing them.
        if (boardId === BoardId.REVIEW) {
            overlay?.classList.add("element-modal-overlay--review");
            modal?.classList.add("element-modal--review");
        }
    }

    /**
     * Spinner replacement: show progress cursor while opening.
     * Keeps token on the instance so close can stop it without clearing global state.
     * @returns {string}
     */
    private startModalOpenBusyCursor(): string {
        if (this._modalOpenBusyToken) {
            busyCursor.stop(this._modalOpenBusyToken);
        }
        const token = busyCursor.start();
        this._modalOpenBusyToken = token;
        return token;
    }

    /**
     * Stop modal open busy cursor.
     * @param {string|null|undefined} token
     */
    private stopModalOpenBusyCursor(token: string | null | undefined): void {
        if (!token) return;
        busyCursor.stop(token);
        if (this._modalOpenBusyToken === token) {
            this._modalOpenBusyToken = null;
        }
    }

    /**
     * Handle modal open
     * @param {HTMLElement} link - Clicked link element
     */
    public async handleOpenModal(link: HTMLElement): Promise<void> {
        this.applyModalContextClasses(link);

        const modalKind = link.dataset.modalKind;
        if (modalKind === "post-create") {
            return this.handleOpenCreateModal(link);
        }

        const postId = this.toPostId(link.dataset.postId);
        const boardId = link.dataset.boardId;

        if (!postId) {
            console.error("Post ID not found");
            return;
        }

        const perfSession = perfLogger.startSession("element-modal-open", {
            postId,
            boardId,
            path: typeof window !== "undefined" ? window.location?.pathname : undefined,
        });
        perfSession?.autoEndAfter(2500);

        if (this.currentlyOpeningPostId === postId) {
            return;
        }

        this.currentlyOpeningPostId = postId;

        // Spinner replacement: show progress cursor while opening.
        const busyToken = this.startModalOpenBusyCursor();

        try {
            // Use data passed from parent page
            const postData = this.syncCountsFromParent(postId);

            perfSession?.mark("counts-synced", {
                hasPostData: !!postData,
            });

            // Check if post detail is already in DOM or cache — if so skip the spinner.
            const contentReady = this.render.hasContentReady(postId);

            // Clear modal body so the previous post never shows through.
            try {
                const modalBody = this.render.getModalBody?.();
                if (modalBody) {
                    perfSession?.mark("modal-body-clear:begin");
                    modalBody.innerHTML = "";
                    perfSession?.mark("modal-body-clear:end");
                }
            } catch (_e) {
                // ignore
            }

            // Apply size before showing so the modal opens at its final dimensions.
            this.render.applyModalSize(postData);
            // Set title before showing so the previous post's title doesn't flash.
            this.render.setModalTitle(link, postData);

            perfSession?.mark("showModal:begin");
            this.render.showModal();
            // Only show spinner when content requires a network fetch.
            // For DOM-cached content the spinner would flash for just one frame — skip it.
            if (!contentReady) {
                this.render.showLoadingSpinner();
            }
            perfSession?.mark("showModal:end");

            // Set current post context early so a quick-close can cancel the async load.
            this.state.setCurrentPost(postId, boardId);
            perfSession?.mark("state:setCurrentPost");

            const run = () => {
                // If the modal was closed before we got here, abort.
                if (!this.render.isOpen() || this.state.currentPostId !== postId) {
                    perfSession?.mark("raf:aborted");
                    this.stopModalOpenBusyCursor(busyToken);
                    return;
                }

                perfSession?.mark("raf:begin");

                // Load and display content (sync DOM move for cached; async fetch fires inside for uncached)
                const tLoad0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                this.render.loadContent(postId, link, postData);
                const tLoad1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                perfSession?.mark("loadContent:done", {
                    ms: Math.round((tLoad1 - tLoad0) * 1000) / 1000,
                });

                // The first parent sync runs before modal content exists.
                // Re-apply after loadContent so the inserted modal DOM reflects the latest parent state.
                if (postData) {
                    this.render.syncLikeCounts(postData);

                    if (typeof postData.reads_count === "number") {
                        this.updateReadDisplays(postId, postData.reads_count);
                    }
                }

                // Mark as read (fire and forget — must not block paint)
                this.markAsReadAndUpdate(postId, boardId, postData).catch((error) => {
                    console.error("Failed to mark as read:", error);
                });

                // Defer secondary init (translations, listeners, SNS card previews) to after
                // the content paint so the modal feels instant and the main thread stays free.
                const afterPaint = () => {
                    const tInit0 =
                        typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                    this.initializeModalContent();
                    const tInit1 =
                        typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                    perfSession?.mark("initializeModalContent:done", {
                        ms: Math.round((tInit1 - tInit0) * 1000) / 1000,
                    });

                    const tSns0 =
                        typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                    this.processSNSPreviews();
                    const tSns1 =
                        typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                    perfSession?.mark("processSNSPreviews:scheduled", {
                        ms: Math.round((tSns1 - tSns0) * 1000) / 1000,
                    });

                    // Stop busy cursor after the next paint so it covers the init frame too.
                    try {
                        requestAnimationFrame(() => {
                            this.stopModalOpenBusyCursor(busyToken);
                            perfSession?.mark("after-paint");
                            perfSession?.end("open-complete");
                            if (perfLogger.activeSession() === perfSession) {
                                perfLogger.clearSession();
                            }
                        });
                    } catch (_e) {
                        setTimeout(() => {
                            this.stopModalOpenBusyCursor(busyToken);
                            perfSession?.mark("after-paint:timeout");
                            perfSession?.end("open-complete");
                            if (perfLogger.activeSession() === perfSession) {
                                perfLogger.clearSession();
                            }
                        }, 0);
                    }
                };

                try {
                    requestAnimationFrame(afterPaint);
                } catch (_e) {
                    setTimeout(afterPaint, 0);
                }
            };

            try {
                if (contentReady) {
                    // Content is in DOM/cache: single rAF so it paints in the very next frame.
                    requestAnimationFrame(() => {
                        perfSession?.mark("raf0-skip");
                        run();
                    });
                } else {
                    // Content needs a network fetch: allow one paint with the spinner visible,
                    // then start the load in the following frame.
                    requestAnimationFrame(() => {
                        perfSession?.mark("raf0");
                        requestAnimationFrame(run);
                    });
                }
            } catch (_e) {
                setTimeout(run, 0);
            }
        } catch (error) {
            const err = error as Error;
            console.error("Error opening modal:", error);
            console.error("Error details:", {
                message: err.message,
                stack: err.stack,
                postId,
                boardId,
            });

            perfSession?.mark("error", {
                message: err.message,
            });
            perfSession?.end("error");
            if (perfLogger.activeSession() === perfSession) {
                perfLogger.clearSession();
            }
            notifier.error("failed_to_load_post");

            this.stopModalOpenBusyCursor(busyToken);

            // If we already showed the modal, close it on failure.
            try {
                if (this.render.isOpen()) {
                    this.handleCloseModal();
                }
            } catch (_e) {
                // ignore
            }
        } finally {
            this.currentlyOpeningPostId = null;
        }
    }

    /**
     * Open create form via XHR and inject into element modal (no page transition).
     * Spinner is shown on the trigger button while fetching (same mechanism as Post submit button).
     */
    private async handleOpenCreateModal(link: HTMLElement): Promise<void> {
        this.applyModalContextClasses(link);

        const boardId = link.dataset.boardId || BoardId.REVIEW;
        const postProductId = link.dataset.postProductId || undefined;
        const modalContentId = this.toModalContentId(link.dataset.modalContentId) ?? `create-${boardId}`;

        const perfSession = perfLogger.startSession("element-modal-open:create", {
            postId: modalContentId,
            boardId,
            path: typeof window !== "undefined" ? window.location?.pathname : undefined,
        });
        perfSession?.autoEndAfter(2500);

        const triggerBtn = link instanceof HTMLButtonElement ? link : link.closest<HTMLButtonElement>("button");
        if (triggerBtn) {
            spinner.showButtonLoader(triggerBtn, { translateKey: "spinner_proccesing" });
        }

        try {
            this.render.applyModalSize({ board_id: "create", board: { modal_size: "extra-large" } });
            this.render.showModal();
            this.render.showLoadingSpinner();

            perfSession?.mark("fetch:begin");
            const doc = await ModalApiService.fetchCreateModalFragment(boardId, postProductId);
            perfSession?.mark("fetch:end");

            if (!this.render.isOpen()) {
                perfSession?.mark("raf:create:aborted");
                return;
            }

            if (!doc) {
                throw new Error("Create modal fragment is empty");
            }

            const postData = { board_id: "create", board: { modal_size: "extra-large" } } as ModalPostData;
            this.render.injectXhrCreateContent(doc, modalContentId, link, postData);

            if (translator) {
                translator.updateUITranslations();
            }

            const modalBody = this.render.getModalBody?.();
            if (modalBody instanceof HTMLElement) {
                modalBody.scrollTop = 0;
            }
            requestAnimationFrame(() => {
                if (modalBody instanceof HTMLElement) {
                    modalBody.scrollTop = 0;
                }
                perfSession?.mark("after-paint");
                perfSession?.end("open-complete");
                if (perfLogger.activeSession() === perfSession) {
                    perfLogger.clearSession();
                }
            });
        } catch (error) {
            console.error("Error opening create modal:", error);
            notifier.error("failed_to_load_post");
            perfSession?.end("open-error");
            if (perfLogger.activeSession() === perfSession) {
                perfLogger.clearSession();
            }
        } finally {
            if (triggerBtn) {
                spinner.hideButtonLoader(triggerBtn);
            }
        }
    }

    /**
     * Handle close request (checks edit mode)
     */
    public handleCloseRequest(): void {
        this.handleCloseModal();
    }

    /**
     * Handle header close (×) click.
     */
    public handleHeaderCloseRequest(): void {
        this.handleCloseModal();
    }

    /**
     * Attempt to cancel the active post edit form inside the element modal.
     * @param {HTMLElement} modal
     * @returns {boolean}
     */
    private tryCancelActiveEdit(modal: HTMLElement): boolean {
        try {
            const editForm = this.modalForm.getVisiblePostEditForm(modal);
            if (!editForm || editForm.style.display === "none") return false;

            const cancelBtn = this.modalForm.getVisiblePostEditCancelButton(modal);
            if (!cancelBtn) return false;

            cancelBtn.click();
            return true;
        } catch (_e) {
            return false;
        }
    }

    /**
     * Handle modal close
     */
    public handleCloseModal(): void {
        this.stopModalOpenBusyCursor(this._modalOpenBusyToken);
        const currentPostId = this.state.currentPostId;
        const modalBody = this.render.getModalBody?.();
        const modal = this.render.getModal?.();

        const perfSession = perfLogger.activeSession();
        perfSession?.mark("closeModal:requested", {
            currentPostId,
        });

        if (modal instanceof HTMLElement) {
            delete modal.dataset.editing;
        }

        // If create form exists in modal, trigger immediate draft save before closing.
        if (modalBody instanceof Element) {
            const createForm = this.modalForm.getPostCreateForm(modalBody);
            if (createForm) {
                try {
                    createForm.dispatchEvent(
                        new CustomEvent("post:create:draft:save", {
                            bubbles: true,
                        }),
                    );
                } catch (_e) {
                    // ignore
                }
            }
        }

        // Cleanup root-bound listeners for modal content
        if (modalBody instanceof Element) {
            disposeEventScope(modalBody, "post-show");
        }

        // Cancel any pending read timers
        if (currentPostId && this.readTimers.has(currentPostId)) {
            clearTimeout(this.readTimers.get(currentPostId));
            this.readTimers.delete(currentPostId);
            this.pendingReadRequests.delete(currentPostId);
        }

        if (currentPostId) {
            PostEditSetup.cleanup(String(currentPostId));
        }

        this.render.closeModal();
        this.state.clearCurrentPost();

        perfSession?.mark("closeModal:done");
    }

    /**
     * Handle card body click (for posts)
     * @param {HTMLElement} cardBody - Card body element
     */
    public handleCardBodyClick(cardBody: HTMLElement): void {
        const modalUsage = cardBody.dataset.modalUsage;
        const boardId = cardBody.dataset.boardId;
        const postId = cardBody.dataset.postId;


        if (modalUsage === "DIRECT_MAXIMIZE") {
            return;
        }

        // MODAL opens in-page; MODELESS_TARGET_BLANK opens a new tab.
        if (modalUsage === "2" || modalUsage === "MODAL") {
            this.handleOpenModal(cardBody);
        } else if (modalUsage === "MODELESS_TARGET_BLANK") {
            const url = `/posts/${boardId}/posts/${postId}`;
            window.open(CommonUtils.resolveUrl(url), "_blank", "noopener");
        } else {
            // Navigate to post detail page (correct Laravel route format)
            const url = `/posts/${boardId}/posts/${postId}`;
            window.location.href = CommonUtils.resolveUrl(url);
        }
    }

    /**
     * Initialize modal content
     */
    private initializeModalContent(): void {
        // Update translations
        if (translator) {
            translator.updateUITranslations();
        }

        // Setup count update listeners
        const modal = this.render.getModal();
        this.state.setupCountUpdateListeners(modal);

        // Setup post show listeners for modal content (edit, delete, like, share buttons)
        this.setupPostShowListeners();
    }

    /**
     * Setup post show event listeners for modal content
     * This ensures edit, delete, like, share buttons work in modal
     */
    private setupPostShowListeners(): void {
        const modal = this.render.getModal();
        if (!modal) return;

        // If PostShowSetup wasn't initialized (e.g., on index pages),
        // we need to initialize it now for modal content
        PostShowModalSetup.init(modal).catch((error) => {
            console.error("Failed to initialize post show for modal:", error);
        });
    }

    /**
     * Sync initial display counts using parent page's postsData
     * @param {string} postId
     * @returns {Object|null} Post data from parent
     */
    private syncCountsFromParent(postId: number): ModalPostData | null {
        const postData = this.state.syncFromParent(postId);
        if (!postData) {
            return null;
        }

        // Sync like counts
        this.render.syncLikeCounts(postData);

        // Sync read count
        if (typeof postData.reads_count === "number") {
            this.updateReadDisplays(postId, postData.reads_count);
        }

        return postData;
    }

    /**
     * Show loading spinner for read count
     */
    private showReadCountLoading(): void {
        const modal = this.render.getModal();
        if (!modal) return;

        const readBtn = this.modalForm.getReadCountElement(modal);
        if (readBtn) {
            readBtn.innerHTML = '<i class="bi bi-arrow-repeat spinner-icon"></i>';
        }
    }

    /**
     * Mark post as read and update counts
     * @param {string} postId - Post ID
     * @param {string} boardId - Board ID
     * @param {Object|null} postData - Post data from parent (optional)
     */
    private async markAsReadAndUpdate(
        postId: number,
        boardId: string,
        postData: ModalPostData | null = null,
    ): Promise<void> {
        if (!postId || !boardId) return;

        try {
            // Check if already recorded by page (prevent duplicate API call)
            const postShowSetup = getPostShowSetup();
            if (postShowSetup?.pageReadRecorded && postShowSetup?.pagePostId === postId) {
                return;
            }

            if (this.pendingReadRequests.has(postId)) {
                return;
            }

            // Skip marking own posts as read
            const postUserId = postData?.user_id;
            const authUser = phpAuthUser();
            const currentUserId = authUser?.user_id;
            if (currentUserId && postUserId && String(currentUserId) === String(postUserId)) {
                return;
            }

            this.pendingReadRequests.add(postId);

            const modalBody = this.render.getModalBody();
            if (!(modalBody instanceof HTMLElement)) {
                return;
            }

            const qualified = await this.waitForQualifiedModalRead(modalBody, postId);
            if (!qualified) {
                return;
            }

            // Check if modal is still open before calling API
            const overlay = this.modalForm.getElementModalOverlay(document);
            if (!overlay || overlay.style.display === "none" || overlay.style.opacity === "0") {
                this.readTimers.delete(postId);
                return;
            }

            this.readTimers.delete(postId);
            const data: ModalReadPayload = await ModalApiService.markAsRead(postId, boardId);

            if (typeof data.reads_count !== "undefined") {
                this.updateReadDisplays(postId, data.reads_count);

                // Update parent post data if available
                if (postData) {
                    postData.reads_count = data.reads_count;
                }

                const postShowApi = postShowSetup?.apiService;
                postShowApi?.setReadRecorded?.(true);
            }
        } catch (error) {
            console.error("Error marking post as read:", error);
        } finally {
            this.pendingReadRequests.delete(postId);
        }
    }

    private waitForQualifiedModalRead(modalBody: HTMLElement, postId: number): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const timerId = setTimeout(() => {
                this.readTimers.delete(postId);
                resolve(true);
            }, ModalHandler.READ_QUALIFICATION_MS);

            this.readTimers.set(postId, timerId);
        });
    }

    /**
     * Update modal and parent read counts in a single place
     * @param {string} postId
     * @param {number} count
     */
    private updateReadDisplays(postId: number, count: number): void {
        const modal = this.render.getModal();
        this.state.updateReadCount(modal, count);
        this.state.updateParentReadCount(postId, count);
    }

    /**
     * Process SNS previews
     */
    private processSNSPreviews(): void {
        const modalBody = this.render.getModalBody();
        ModalApiService.processSNSPreviews(modalBody);
    }

    /**
     * Public API: Update parent like count
     * Called from like-ui.js
     * @param {number} postId - Post ID
     * @param {number} count - Like count
     */
    public updateParentLikeCount(postId: number, count: number): void {
        if (!Number.isInteger(postId) || postId <= 0) return;
        this.state.updateParentLikeCount(postId, count);
    }

    /**
     * Public API: Update parent like state
     * Called from like-ui.js
     * @param {number} postId - Post ID
     * @param {boolean} isLiked - Like state
     */
    public updateParentLikeState(postId: number, isLiked: boolean): void {
        if (!Number.isInteger(postId) || postId <= 0) return;
        this.state.updateParentLikeState(postId, isLiked);
    }

    /**
     * Public API: Check if modal is open
     * @returns {boolean}
     */
    public isOpen(): boolean {
        return this.render.isOpen();
    }
}

// Singleton instance
let modalHandler: ModalHandler | null = null;

/**
 * Set modal handler singleton instance.
 * Kept as a live binding so other modules importing { modalHandler } see updates.
 * @param {ModalHandler|null} handler
 */
export function setModalHandlerInstance(handler: ModalHandler | null): void {
    modalHandler = handler;
}

export { ModalHandler, modalHandler };
